import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState, useCallback } from "react";

import { Stack } from "@mui/system";
import { Card, Avatar, Button, Tooltip, TextField, IconButton, Typography, CardContent, Autocomplete, ListItemText } from "@mui/material";

import { paths } from "src/routes/paths";
import { useRouter } from "src/routes/hooks";

import { useBoolean } from "src/hooks/use-boolean";
import { useAuthContext } from "src/hooks/auth/use-auth-context";

import { useTranslate } from "src/locales";
import { EntityLinkType } from "src/services/common.types";
import { IContactListItem, ContactLinkEntityType } from "src/services/contacts/contacts.types";
import { useGetCompanyContactLinksQuery } from "src/services/connections/connections.service";
import { useAddContactLinkMutation, useGetOrganizationContactsLeanQuery } from "src/services/contacts/contacts.service";
import { PICKER_PAGE, pickerSortConfig, pickerHelperText, pickerNoOptionsText, buildPickerColumnFilters } from "src/utils/picker-search";

import Iconify from "src/components/iconify";
import { ConfirmDialog } from "src/components/custom-dialog";
import TruncatedTextField from "src/components/truncate-text/truncated-text-field";

type Props = {
  profileId: string;
}

type PickableContact = Pick<IContactListItem, 'id' | 'first_name' | 'last_name'>;

const DEFAULT_PAGE_OPTIONS = {
  page: 1,
  per_page: 5
};

const PrimaryContactCard = ({ profileId }: Props) => {

  const { t } = useTranslate();

  const authContext = useAuthContext();

  const editPrimaryContactControl = useBoolean(false);

  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [selectedContact, setSelectedContact] = useState<PickableContact | null>(null);

  const router = useRouter();

  const [addContactLink, { isLoading: isAddingContactLinks }] = useAddContactLinkMutation();

  const { data: contactLinks } = useGetCompanyContactLinksQuery({
    profile_id: profileId,
    params: DEFAULT_PAGE_OPTIONS
  }, {
    skip: !profileId,
  });

  // Name/email search on the lean list, only while the edit dialog is open (JT1-1586). Any contact can
  // become primary, so nothing is shown as unavailable.
  const { currentData: contacts, isFetching: isSearchingContacts, isError: isContactSearchError } = useGetOrganizationContactsLeanQuery(
    {
      org_id: authContext.organization.id,
      page: PICKER_PAGE,
      sort_config: pickerSortConfig(contactSearchQuery),
      column_filters: buildPickerColumnFilters(contactSearchQuery, { email: true }),
    },
    {
      skip: !editPrimaryContactControl.value,
    }
  );

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setContactSearchQuery(value);
    }, 500),
    [setContactSearchQuery]
  );

  const onLinkModalClose = useCallback(() => {
    editPrimaryContactControl.onFalse();
    setSelectedContact(null);
    setContactSearchQuery("");
  }, [editPrimaryContactControl, setSelectedContact, setContactSearchQuery]);

  const handleUpdatePrimaryContact = useCallback(async () => {

    if (!selectedContact) return;

    try {
      await addContactLink({
        data: {
          entity_id: profileId,
          entity_type: ContactLinkEntityType.COMPANY_PROFILE,
          contacts: [{
            type: EntityLinkType.PRIMARY,
            id: selectedContact.id,
          }]
        }
      }).unwrap();

      onLinkModalClose();

      enqueueSnackbar(t('companies.profile.api.update_primary_contact.success'), { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar(t('companies.profile.api.update_primary_contact.default_error'), { variant: 'error' });
    }
  }, [onLinkModalClose, addContactLink, profileId, selectedContact, t]);

  const primaryContactLink = useMemo(() => contactLinks?.results?.find((contactLink) => contactLink.link_type === EntityLinkType.PRIMARY) ?? null, [contactLinks]);

  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction="row" spacing={2} display="flex" alignItems="center">
            <Avatar
              alt={primaryContactLink?.first_name ?? ""}
              variant="rounded"
              sx={{ width: 48, height: 48 }}
            >
              {
                !primaryContactLink && (
                  <Iconify icon="ep:question-filled" sx={{ width: 32, height: 32 }} />
                )
              }

            </Avatar>

            <Stack direction="row" display="flex" width="100%" justifyContent="space-between">
              <ListItemText
                primary={t('companies.profile.primary_contact.title')}
                secondary={
                  <TruncatedTextField
                    text={primaryContactLink ? `${primaryContactLink.first_name} ${primaryContactLink.last_name}` : t('companies.profile.primary_contact.no_primary_contact')}
                    limit={15}
                    mode="characters"
                    showMoreEnabled={false}
                    variant="body2"
                    onClick={() => {
                      if (primaryContactLink) {
                        router.push(paths.dashboard.contacts.detail(primaryContactLink?.id as string));
                      }
                    }}
                    sx={{ '&:hover': primaryContactLink ? { cursor: 'pointer', textDecoration: 'underline' } : {} }}
                  />
                }
              />

              <Stack direction="row" spacing={2} display="flex" alignItems="center">
                <Tooltip title={t('companies.profile.primary_contact.update_tooltip')} arrow placement="top">
                  <IconButton onClick={editPrimaryContactControl.onTrue}>
                    <Iconify icon="solar:pen-bold" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>

      <ConfirmDialog
        open={editPrimaryContactControl.value}
        title={t('companies.profile.primary_contact.edit')}
        onClose={onLinkModalClose}
        content={
          <Autocomplete
            sx={{
              paddingTop: 2,
            }}
            multiple={false}
            options={(contacts?.results ?? []) as PickableContact[]}
            value={selectedContact}
            // The server already matched the text; MUI's default label filter would drop its results.
            filterOptions={(options) => options}
            loading={isSearchingContacts}
            noOptionsText={pickerNoOptionsText(t, isContactSearchError)}
            // 'reset' is MUI re-syncing the input after a selection or re-render, not the user typing.
            onInputChange={(_e, value, reason) => reason !== 'reset' && onSearchQueryChange(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t(
                  'companies.profile.primary_contact.search'
                )}
                placeholder={t(
                  'companies.profile.primary_contact.search_placeholder'
                )}
                helperText={pickerHelperText(t, contacts)}
              />
            )}
            onChange={(_e, value) => setSelectedContact(value)}
            renderOption={({ key, ...rest }, option) => (
              <li key={key} {...rest}>
                <Stack direction="column">
                  <Typography variant="body1">
                    {`${option.first_name} ${option.last_name}`}
                  </Typography>
                </Stack>
              </li>
            )}
            getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionKey={(option) => option.id}
          />
        }
        action={
          <Button variant="contained" disabled={!selectedContact || isAddingContactLinks} onClick={handleUpdatePrimaryContact}>
            {t('common.update')}
          </Button>
        }
      />
    </Card>
  )

}

export default PrimaryContactCard;
